/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/esm/ArrowForwardIosSharp';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { Typography } from '@mui/material';

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
	border: `1px solid ${theme.palette.divider}`,
	maxHeight: 'unset',
	'&:not(:last-child)': {
		borderBottom: 0
	},
	'&:before': {
		display: 'none'
	}
}));

const AccordionSummary = styled((props) => (
	<MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
	fontWeight: 'bold',
	backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .02)',
	flexDirection: 'row-reverse',
	'& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
		transform: 'rotate(90deg)'
	},
	'& .MuiAccordionSummary-content': {
		marginLeft: theme.spacing(1)
	}
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
	padding: theme.spacing(1),
	borderTop: '1px solid rgba(0, 0, 0, .125)'
}));

function StreamSheetAccordionComponent(props) {
	return (
		<Accordion bottom={props.bottom} onChange={props.onChange} id={props.id} expanded={props.expanded}>
			<AccordionSummary sx={{minHeight: '40px', height: '40px'}}>
				<Typography
					sx={{
						width: props.subTitle ? '33%' : null,
						flexShrink: 0,
						fontWeight: 500
					}}
				>
					{props.title}
				</Typography>
				{props.subTitle ? <Typography sx={{ color: 'text.secondary' }}>{props.subTitle}</Typography> : null}
			</AccordionSummary>
			<AccordionDetails>
				{props.details ? <Typography sx={{mb: '10px'}}>{props.details}</Typography> : null}
				{props.children}
			</AccordionDetails>
		</Accordion>
	);
}

StreamSheetAccordionComponent.propTypes = {
	onChange: PropTypes.func.isRequired,
	title: PropTypes.object.isRequired,
	subTitle: PropTypes.object,
	expanded: PropTypes.bool.isRequired,
	details: PropTypes.object
};

StreamSheetAccordionComponent.defaultProps = {
	details: null,
	subTitle: null
};

export const StreamSheetAccordion = StreamSheetAccordionComponent;
